import type { PublicPlayer } from 'shared';
import { Avatar } from '../design/Avatar';
import { Panel } from '../design/Panel';
import { Label } from '../design/Paper';
import { Stamp } from '../design/Stamp';
import { tiltFor } from '../design/tokens';
import { ro } from '../i18n/ro';

export interface GovernmentPair {
  president: PublicPlayer | null;
  chancellor: PublicPlayer | null;
}

/**
 * The president/chancellor pair as portraits. Used where the government is the
 * subject of the screen — a proposal being voted on.
 */
export function GovernmentBlock({
  pair,
  termLimitedIds = [],
}: {
  pair: GovernmentPair;
  termLimitedIds?: string[];
}) {
  const seats: Array<[string, string, PublicPlayer | null]> = [
    ['president', ro.president, pair.president],
    ['chancellor', ro.chancellor, pair.chancellor],
  ];

  return (
    <div className="flex items-start justify-center gap-scr-4">
      {seats.map(([seat, role, person]) => (
        <div
          key={role}
          className="flex min-w-0 flex-col items-center"
          data-testid={`gov-${seat}`}
          data-name={person?.name ?? ''}
        >
          <Panel press="lg" rule innerClassName="p-scr-1" className={tiltFor(role)}>
            {person ? (
              <Avatar subject={person} size="hero" dimmed={!person.alive} />
            ) : (
              <div className="h-[15cqh] w-[15cqh] bg-paper-sunk" />
            )}
          </Panel>
          <div className="mt-scr-2 max-w-[34cqw] truncate font-display text-screen-lg uppercase leading-none">
            {person?.name ?? '—'}
          </div>
          <Label className="mt-scr-1 text-screen-sm text-ink-soft">{role}</Label>

          {/* Warnings, never blocks: the admin is always the authority. */}
          <div className="mt-scr-1 flex gap-[0.8cqw]">
            {person && !person.alive && (
              <Stamp tone="red" size="sm">
                {ro.executed}
              </Stamp>
            )}
            {person && termLimitedIds.includes(person.id) && (
              <Stamp tone="ink" size="sm">
                {ro.termLimited}
              </Stamp>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * The same pair as a compact horizontal band, for screens where the ballot row
 * is the subject and the government is context. Three portrait-sized registers
 * do not fit a 16:9 frame, and the register carrying the votes must not shrink.
 */
export function GovernmentBand({ pair }: { pair: GovernmentPair }) {
  const seats: Array<[string, string, PublicPlayer | null]> = [
    ['president', ro.president, pair.president],
    ['chancellor', ro.chancellor, pair.chancellor],
  ];

  return (
    <div className="flex shrink-0 items-center justify-center gap-scr-4">
      {seats.map(([seat, role, person]) => (
        <div
          key={role}
          className="flex min-w-0 items-center gap-[1.2cqw]"
          data-testid={`gov-${seat}`}
          data-name={person?.name ?? ''}
        >
          <Panel press="sm" rule innerClassName="p-[0.5cqh]" className={tiltFor(role)}>
            {person ? (
              <Avatar
                subject={person}
                size="hero"
                dimmed={!person.alive}
                className="h-[10cqh] w-[10cqh]"
              />
            ) : (
              <div className="h-[10cqh] w-[10cqh] bg-paper-sunk" />
            )}
          </Panel>
          <div className="min-w-0">
            <div className="max-w-[24cqw] truncate font-display text-screen-md uppercase leading-none">
              {person?.name ?? '—'}
            </div>
            <Label className="mt-[0.6cqh] block text-screen-sm text-ink-soft">{role}</Label>
          </div>
        </div>
      ))}
    </div>
  );
}
